import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  BsArrowLeft,
  BsCheck,
  BsStar,
  BsStarFill,
  BsBagCheck,
  BsClockHistory,
  BsReceipt,
  BsCreditCard,
  BsGeoAlt,
  BsPerson,
  BsPlus,
  BsCamera,
  BsArrowCounterclockwise,
  BsX,
} from "react-icons/bs";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "../../../../assets/css/customer/subscreens/account/OrderDetails.css";
import AppContext from "../../../../contexts/AppContext";
import LoadingScreen from "../../../../components/general/animations/Animations.js";
import MyNavbar from "../../../../components/general/mynavbar/MyNavbar.js";

const MAX_REVIEW_IMAGES = 4;
const MAX_RETURN_IMAGES = 4;

const RETURN_REASONS = ["Damaged Product", "Wrong Item Received", "Other"];

const OrderDetails = () => {
  const navigate = useNavigate();

  const chatEndRef = useRef(null);
  const reviewFileInputRef = useRef(null);
  const returnFileInputRef = useRef(null);

  const {
    selectedOrder,
    baseUrl,
    baseUrlForFiles,
    setSelectedOrder,
    customerAuthToken,
  } = useContext(AppContext);
  const savedCustomer = JSON.parse(localStorage.getItem("loggedInCustomer"));

  const [loading, setLoading] = useState(true);

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [reviewImages, setReviewImages] = useState([]);
  const [reviewImagePreviews, setReviewImagePreviews] = useState([]);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  const [previewImage, setPreviewImage] = useState(null);

  // Chat messages for the "customized product" chat thread

  // Custom order creation modal (used from the chat flow)
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [chatOrderData, setChatOrderData] = useState({
    itemName: "",
    price: "",
    deliveryTime: "",
    description: "",
  });

  const [chatMessages, setChatMessages] = useState([]);

  // ── Return Request state ──
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [returnDescription, setReturnDescription] = useState("");
  const [returnImages, setReturnImages] = useState([]);
  const [returnImagePreviews, setReturnImagePreviews] = useState([]);
  const [returnSubmitting, setReturnSubmitting] = useState(false);

  // ── Image lightbox state (used for both review + return images) ──
  const [lightboxImage, setLightboxImage] = useState(null);

  const normalizeReview = (review) => {
    if (!review) return null;
    return {
      ...review,
      images: (review.images || []).map((img) =>
        typeof img === "string" ? img : img.image_path,
      ),
    };
  };

  const normalizeReturn = (return_request) => {
    if (!return_request) return null;
    return {
      ...return_request,
      images: (return_request.images || []).map((img) =>
        typeof img === "string" ? img : img.image_path,
      ),
    };
  };

  const [submittedReview, setSubmittedReview] = useState(
    normalizeReview(selectedOrder?.review),
  );

  const [submittedReturn, setSubmittedReturn] = useState(
    normalizeReturn(selectedOrder?.return_request),
  );

  const fetchMessages = () => {
    axios
      .get(`${baseUrl}/order/chat/getMessages`, {
        params: { order_id: selectedOrder?.id },
        headers: {
          Authorization: `Bearer ${customerAuthToken}`,
        },
      })
      .then((res) => setChatMessages(res.data?.messages || []))
      .catch(() => {});
  };

  useEffect(() => {
    if (!selectedOrder) {
      navigate("/orders");
    } else {
      setSubmittedReview(normalizeReview(selectedOrder.review));
      fetchMessages();
      const timer = setTimeout(() => setLoading(false), 2500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openProductChat = (product) => {
    if (selectedOrder.status === "Cancelled") {
      toast.error("Chat is not available for cancelled orders.");
      return;
    }

    const sellerId = product?.seller_id || product?.seller?.id;
    if (!product?.id || !sellerId) {
      toast.error("Unable to open chat for this product.");
      return;
    }

    const productForChat = { ...product, seller_id: sellerId };

    navigate("/product/chat-seller", {
      state: { selectedProductForCustomer: productForChat },
    });
  };

  useEffect(() => {
    if (!selectedOrder) {
      navigate("/orders");
    } else {
      setSubmittedReview(normalizeReview(selectedOrder.review));
      setSubmittedReturn(normalizeReturn(selectedOrder.return_request));
      const timer = setTimeout(() => setLoading(false), 2500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrder]);

  // ── Review image handlers ──

  const handleReviewImageSelect = (e) => {
    const files = Array.from(e.target.files).slice(0, MAX_REVIEW_IMAGES);
    const previews = files.map((file) => URL.createObjectURL(file));
    setReviewImages(files);
    setReviewImagePreviews(previews);
  };

  const removeReviewImage = (index) => {
    setReviewImages(reviewImages.filter((_, i) => i !== index));
    setReviewImagePreviews(reviewImagePreviews.filter((_, i) => i !== index));
  };

  const clearReviewImages = () => {
    setReviewImages([]);
    setReviewImagePreviews([]);
  };

  const handleReviewSubmit = () => {
    if (rating === 0) {
      toast.error("Please select a star rating!");
      return;
    }
    if (reviewText.trim().length < 5) {
      toast.error("Please write a review!");
      return;
    }
    setReviewSubmitting(true);
    const product = selectedOrder?.cart?.[0]?.product;
    const formdata = new FormData();
    formdata.append("orderid", selectedOrder?.id);
    formdata.append("customerid", savedCustomer?.id);
    formdata.append("rating", rating);
    formdata.append("comment", reviewText);
    formdata.append("product_id", product.id);
    reviewImages.forEach((file) => {
      formdata.append("review_images[]", file);
    });
    axios
      .post(`${baseUrl}/customer/order/addReview`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${customerAuthToken}`,
        },
      })
      .then((res) => {
        if (res.data.success === false) {
          toast.error(res.data.message || "Failed to submit review.");
          setReviewSubmitting(false);
          return;
        }
        toast.success("Review submitted successfully!");
        const newReview = {
          rating,
          comment: reviewText,
          images: res.data?.images || [],
        };
        setSubmittedReview(newReview);
        if (setSelectedOrder)
          setSelectedOrder((prev) => ({ ...prev, review: newReview }));
        setRating(0);
        setReviewText("");
        clearReviewImages();
        setReviewSubmitting(false);
        setShowReviewModal(false);
      })
      .catch(() => {
        toast.error("Failed to submit review. Try again.");
        setReviewSubmitting(false);
      });
  };

  const closeReviewModal = () => {
    setShowReviewModal(false);
    setRating(0);
    setHoverRating(0);
    setReviewText("");
    clearReviewImages();
  };

  // --- Custom order creation (from the product chat flow) ---
  // NOTE: endpoint/payload below is a reasonable guess based on the form
  // fields already in the JSX. Confirm the real API route + field names
  // with your backend before relying on this.
  const handleOrderSubmit = (e) => {
    e.preventDefault();

    if (
      !chatOrderData.itemName.trim() ||
      !chatOrderData.price ||
      !chatOrderData.deliveryTime.trim()
    ) {
      toast.error("Please fill in all required fields.");
      return;
    }

    axios
      .post(`${baseUrl}/order/chat/createCustomOrder`, {
        order_id: selectedOrder?.id,
        item_name: chatOrderData.itemName,
        price: chatOrderData.price,
        delivery_time: chatOrderData.deliveryTime,
        description: chatOrderData.description,
      })
      .then((res) => {
        if (res.data?.success === false) {
          toast.error(res.data.message || "Failed to create custom order.");
          return;
        }
        toast.success("Custom order created!");
        setChatOrderData({
          itemName: "",
          price: "",
          deliveryTime: "",
          description: "",
        });
        setShowOrderModal(false);
        fetchMessages();
      })
      .catch(() => {
        toast.error("Failed to create custom order. Try again.");
      });
  };

  // ── Return Request handlers (simple — no preview, just store the files) ──

  const handleReturnImageSelect = (e) => {
    const files = Array.from(e.target.files).slice(0, MAX_RETURN_IMAGES);
    const previews = files.map((file) => URL.createObjectURL(file));
    setReturnImages(files);
    setReturnImagePreviews(previews);
  };

  const removeReturnImage = (index) => {
    setReturnImages(returnImages.filter((_, i) => i !== index));
    setReturnImagePreviews(returnImagePreviews.filter((_, i) => i !== index));
  };

  const clearReturnImages = () => {
    setReturnImages([]);
    setReturnImagePreviews([]);
  };

  const handleReturnSubmit = () => {
    if (!returnReason) {
      toast.error("Please select a reason for return!");
      return;
    }
    if (returnDescription.trim().length < 5) {
      toast.error("Please describe the issue!");
      return;
    }
    if (returnImages.length === 0) {
      toast.error("Please add at least one photo as proof!");
      return;
    }
    setReturnSubmitting(true);
    const formdata = new FormData();
    formdata.append("chorder_id", selectedOrder?.id);
    formdata.append("chcustomer_id", savedCustomer?.id);
    formdata.append("reason", returnReason);
    formdata.append("description", returnDescription);
    returnImages.forEach((file) => {
      formdata.append("return_images[]", file);
    });
    axios
      .post(`${baseUrl}/customer/return/requestReturn`, formdata, {

        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${customerAuthToken}`,
        },

      })
      .then((res) => {
        if (res.data.success === false) {
          toast.error(res.data.message || "Failed to submit return request.");
          setReturnSubmitting(false);
          return;
        }
        toast.success("Return request submitted successfully!");
        const newReturn = {
          id: res.data.return_id,
          reason: returnReason,
          description: returnDescription,
          status: "Pending",
          images: res.data?.images || [],
        };
        setSubmittedReturn(newReturn);
        if (setSelectedOrder)
          setSelectedOrder((prev) => ({ ...prev, return_request: newReturn }));
        setReturnReason("");
        setReturnDescription("");
        // Don't revoke the blob URLs here — they're now referenced by
        // submittedReturn.images and shown in the status card above.
        setReturnImages([]);
        setReturnImagePreviews([]);
        setReturnSubmitting(false);
        setShowReturnModal(false);
      })
      .catch(() => {
        toast.error("Failed to submit return request. Try again.");
        setReturnSubmitting(false);
      });
  };

  const closeReturnModal = () => {
    setShowReturnModal(false);
    setReturnReason("");
    setReturnDescription("");
    clearReturnImages();
  };

  const getStatusClass = (status) => {
    const map = {
      Processing: "od-status-processing",
      completed: "od-status-delivered",
      Shipped: "od-status-shipped",
      Cancelled: "od-status-cancelled",
    };
    return map[status] || "";
  };

  const getReturnStatusClass = (status) => {
    const map = {
      Pending: "od-return-status--pending",
      Cancelled: "od-return-status--cancelled",
      Returned: "od-return-status--returned",
    };
    return map[status] || "";
  };

  if (loading) return <LoadingScreen />;
  if (!selectedOrder) return null;

  const renderStars = (count) => {
    const stars = [];
    for (let s = 1; s <= 5; s++) {
      if (s <= count) {
        stars.push(<BsStarFill key={s} className="od-star-filled-sm" />);
      } else {
        stars.push(<BsStar key={s} className="od-star-empty-sm" />);
      }
    }
    return stars;
  };

  // ── Order summary breakdown ──
  const subtotal = (selectedOrder.cart || []).reduce(
    (sum, item) => sum + Number(item.price) * Number(item.quantity),
    0,
  );
  const shippingFee = Number(selectedOrder.shipping_fee || 0);

  const timelineSteps = [
    { label: "Order placed", completed: true },
    { label: "Processing", completed: selectedOrder.status !== "Cancelled" },
    {
      label: "Shipped",
      completed:
        selectedOrder.status === "Shipped" ||
        selectedOrder.status === "completed",
    },
    { label: "Delivered", completed: selectedOrder.status === "completed" },
  ];

  // ── Return eligibility: completed + no existing request + within 24 hours ──
  const orderDate = new Date(selectedOrder.updated_at);
  const now = new Date();
  const hoursSinceDelivery = (now - orderDate) / (1000 * 60 * 60);
  const returnWindowOpen = hoursSinceDelivery <= 24;
  const showReturnAction =
    selectedOrder.status === "completed" && !submittedReturn;

  return (
    <>
      <MyNavbar />
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="od-orders-page">
        <div className="od-orders-container">
          <div className="od-account-shell-container">
            <div className="od-details-header">
              <div className="od-header-left">
                <button
                  className="od-back-arrow-btn"
                  onClick={() => navigate("/orders")}
                  aria-label="Back to orders"
                >
                  <BsArrowLeft />
                </button>
                <div>
                  <h1 className="od-details-title">Order Details</h1>
                  <div className="od-order-meta">
                    <span className="od-order-id">
                      Order #{selectedOrder.id}
                    </span>
                    <span className="od-meta-dot">•</span>
                    <span className="od-order-date">
                      {new Date(selectedOrder.created_at).toLocaleDateString(
                        "en-PK",
                        { day: "numeric", month: "short", year: "numeric" },
                      )}
                    </span>
                    <span
                      className={`od-order-status-badge ${getStatusClass(selectedOrder.status)}`}
                    >
                      {selectedOrder.status}
                    </span>
                  </div>
                </div>
              </div>
              <div className="od-header-actions-group">
                {selectedOrder.status === "completed" && !submittedReview && (
                  <button
                    className="od-add-review-btn"
                    onClick={() => setShowReviewModal(true)}
                  >
                    <BsStarFill className="od-add-review-btn-icon" /> Add review
                  </button>
                )}
                {showReturnAction && (
                  <div className="od-return-action-col">
                    <button
                      className="od-request-return-btn"
                      onClick={() => setShowReturnModal(true)}
                      disabled={!returnWindowOpen}
                    >
                      <BsArrowCounterclockwise className="od-add-review-btn-icon" />{" "}
                      Return Request
                    </button>
                    <span className="od-return-window-note">
                      {returnWindowOpen
                        ? "Within 24 hours of delivery"
                        : "24-hour return window has passed"}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {selectedOrder.status === "completed" && submittedReview && (
              <div className="od-submitted-review-card">
                <div className="od-submitted-review-header">
                  <span className="od-submitted-review-label">Your review</span>
                  <div className="od-submitted-review-stars">
                    {renderStars(submittedReview.rating)}
                  </div>
                </div>
                <p className="od-submitted-review-comment">
                  {submittedReview.comment}
                </p>
                {submittedReview.images?.length > 0 && (
                  <div className="od-submitted-review-images">
                    {submittedReview.images.map((img, idx) => (
                      <img
                        key={idx}
                        src={`${baseUrlForFiles}/${img}`}
                        alt={`Review ${idx + 1}`}
                        className="od-submitted-review-image"
                        onClick={() =>
                          setLightboxImage(`${baseUrlForFiles}/${img}`)
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {submittedReturn && (
              <div className="od-return-status-card">
                <div className="od-return-status-header">
                  <span className="od-return-status-label">Return Request</span>
                  <span
                    className={`od-return-status-badge ${getReturnStatusClass(submittedReturn.status)}`}
                  >
                    {submittedReturn.status}
                  </span>
                </div>
                <p className="od-return-status-reason">
                  {submittedReturn.reason}
                </p>
                <p className="od-return-status-description">
                  {submittedReturn.description}
                </p>
                {submittedReturn.images?.length > 0 && (
                  <div className="od-submitted-review-images">
                    {submittedReturn.images.map((img, idx) => (
                      <img
                        key={idx}
                        src={`${baseUrlForFiles}/${img}`}
                        alt={`Return proof ${idx + 1}`}
                        className="od-submitted-review-image"
                        onClick={() =>
                          setLightboxImage(`${baseUrlForFiles}/${img}`)
                        }
                      />
                    ))}
                  </div>
                )}
                {submittedReturn.seller_response && (
                  <p className="od-return-seller-response">
                    <strong>Seller response:</strong>{" "}
                    {submittedReturn.seller_response}
                  </p>
                )}
              </div>
            )}

            <div className="od-details-grid">
              <div className="od-details-left-col">
                <div className="od-details-card">
                  <h2 className="od-card-title">
                    <BsBagCheck className="od-card-title-icon" /> Order items
                  </h2>
                  <div className="od-items-list">
                    {(selectedOrder.cart || []).map((item) => {
                      let imgSrc = "/placeholder.png";
                      if (item.product?.images?.length > 0) {
                        imgSrc = `${baseUrlForFiles}/${item.product.images[0].image_path}`;
                      }
                      const isClickable =
                        item.isCustomized == 1 &&
                        selectedOrder.status !== "Cancelled";
                      return (
                        <div
                          key={item.id}
                          className={`od-item-row ${isClickable ? "od-item-row--clickable" : ""}`}
                          onClick={
                            isClickable
                              ? () => openProductChat(item.product)
                              : undefined
                          }
                        >
                          <img
                            src={imgSrc}
                            alt={item.product?.product_name}
                            className="od-item-image"
                          />
                          <div className="od-item-info">
                            <h3 className="od-item-name">
                              {item.product?.product_name}
                              {item.isCustomized == 1 && (
                                <span className="od-customized-badge">
                                  Customized
                                </span>
                              )}
                            </h3>
                            {(item.size || item.product?.variant?.colors) && (
                              <div className="od-item-variants">
                                {item.size &&
                                  item.size !== "null" &&
                                  item.size !== "" && (
                                    <span>Size: {item.size}</span>
                                  )}
                                {item.product?.variant?.colors &&
                                  item.product.variant.colors.trim() !== "" && (
                                    <span className="od-color-wrap">
                                      Color:
                                      <span
                                        className="od-color-dot"
                                        style={{
                                          display: "inline-block",
                                          width: 12,
                                          height: 12,
                                          borderRadius: "50%",
                                          marginLeft: 4,
                                          verticalAlign: "middle",
                                          border: "1px solid #ccc",
                                          backgroundColor:
                                            item.product.variant.colors.split(
                                              ",",
                                            )[item.color] ?? "transparent",
                                        }}
                                      />
                                    </span>
                                  )}
                              </div>
                            )}
                            <p className="od-item-quantity">
                              Qty: {item.quantity}
                            </p>
                          </div>
                          <div className="od-item-price">
                            <span className="od-price-label">Unit price</span>
                            <span className="od-price-value">
                              Rs {Number(item.price).toLocaleString()}
                            </span>
                            {item.quantity > 1 && (
                              <span className="od-total-price">
                                Total: Rs{" "}
                                {(item.price * item.quantity).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="od-details-card">
                  <h2 className="od-card-title">
                    <BsClockHistory className="od-card-title-icon" /> Order
                    timeline
                  </h2>
                  <div className="od-timeline-container">
                    {timelineSteps.map((step, index) => (
                      <div
                        key={index}
                        className={`od-timeline-item ${step.completed ? "completed" : "pending"}`}
                      >
                        <div className="od-timeline-dot">
                          {step.completed && <BsCheck />}
                        </div>
                        <div className="od-timeline-content">
                          <p className="od-timeline-status">{step.label}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="od-details-right-col">
                <div className="od-details-card">
                  <h2 className="od-card-title">
                    <BsReceipt className="od-card-title-icon" /> Order summary
                  </h2>
                  <div className="od-summary-rows">
                    <div className="od-summary-row">
                      <span>
                        Subtotal ({(selectedOrder.cart || []).length} item
                        {(selectedOrder.cart || []).length !== 1 ? "s" : ""})
                      </span>
                      <span>Rs {subtotal.toLocaleString()}</span>
                    </div>
                    <div className="od-summary-row">
                      <span>Shipping Fee</span>
                      <span>Rs {shippingFee.toLocaleString()}</span>
                    </div>
                    <div className="od-summary-row total">
                      <span>Total</span>
                      <span>
                        Rs{" "}
                        {Number(
                          selectedOrder.total_amount || 0,
                        ).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="od-details-card">
                  <h2 className="od-card-title">
                    <BsCreditCard className="od-card-title-icon" /> Payment
                    method
                  </h2>
                  <div className="od-payment-method-wrap">
                    <div className="od-payment-icon">
                      <BsCreditCard />
                    </div>
                    <p className="od-payment-text">
                      {selectedOrder.payment_method || "Cash on delivery"}
                    </p>
                  </div>
                </div>

                <div className="od-details-card">
                  <h2 className="od-card-title">
                    <BsGeoAlt className="od-card-title-icon" /> Delivery address
                  </h2>
                  <div className="od-address-content">
                    <p className="od-address-name">{selectedOrder.full_name}</p>
                    <p className="od-address-line">{selectedOrder.address}</p>
                    <p className="od-address-phone">{selectedOrder.phone}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Add/View Review Modal ── */}
      {showReviewModal && (
        <div className="od-modal-overlay" onClick={closeReviewModal}>
          <div
            className="od-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="od-modal-header">
              <h3>Rate your order</h3>
              <button className="od-modal-close-btn" onClick={closeReviewModal}>
                ✕
              </button>
            </div>
            <div className="od-review-modal-body">
              <p className="od-review-modal-sub">
                Share your experience with order #{selectedOrder.id}
              </p>
              <div className="od-review-stars-row-main">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    className="od-review-star-main"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                  >
                    {(hoverRating || rating) >= star ? (
                      <BsStarFill className="od-star-filled" />
                    ) : (
                      <BsStar className="od-star-empty" />
                    )}
                  </span>
                ))}
              </div>
              <p className="od-rating-label">
                {rating === 1 && "Poor"}
                {rating === 2 && "Fair"}
                {rating === 3 && "Good"}
                {rating === 4 && "Very good"}
                {rating === 5 && "Excellent!"}
              </p>
              <div className="od-review-input-wrap">
                <textarea
                  className="od-review-textarea"
                  placeholder="Write your review here..."
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  rows={4}
                />

                {/* ── Review Images ── */}
                <div className="od-review-images-section">
                  <label className="od-review-images-label">
                    Add photos (optional, up to {MAX_REVIEW_IMAGES})
                  </label>
                  <div className="od-review-images-row">
                    {reviewImagePreviews.map((src, idx) => (
                      <div key={idx} className="od-review-image-thumb">
                        <img src={src} alt={`Review upload ${idx + 1}`} />
                        <button
                          type="button"
                          className="od-review-image-remove"
                          onClick={() => removeReviewImage(idx)}
                          aria-label="Remove image"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    {reviewImages.length < MAX_REVIEW_IMAGES && (
                      <label
                        htmlFor="review-image-input"
                        className="od-review-image-add-btn"
                      >
                        <BsCamera />
                        <span>Add photo</span>
                      </label>
                    )}
                  </div>
                  <input
                    type="file"
                    id="review-image-input"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleReviewImageSelect}
                  />
                </div>
              </div>
              <div className="od-modal-actions">
                <button
                  type="button"
                  className="od-btn-cancel"
                  onClick={closeReviewModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="od-btn-submit"
                  onClick={handleReviewSubmit}
                  disabled={reviewSubmitting}
                >
                  {reviewSubmitting ? "Sending..." : "Send review"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Request Return Modal ── */}
      {showReturnModal && (
        <div className="od-modal-overlay" onClick={closeReturnModal}>
          <div
            className="od-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="od-modal-header">
              <h3>Return Request</h3>
              <button className="od-modal-close-btn" onClick={closeReturnModal}>
                ✕
              </button>
            </div>
            <div className="od-review-modal-body">
              <p className="od-review-modal-sub">
                Tell us what went wrong with order #{selectedOrder.id}. Our
                seller will review your request.
              </p>

              <div className="od-review-input-wrap">
                <select
                  className="od-return-reason-select"
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                >
                  <option value="">Select a reason</option>
                  {RETURN_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>

                <textarea
                  className="od-review-textarea"
                  placeholder="Describe the issue in detail..."
                  value={returnDescription}
                  onChange={(e) => setReturnDescription(e.target.value)}
                  rows={4}
                />

                {/* ── Return Proof Images ── */}
                <div className="od-review-images-section">
                  <label className="od-review-images-label">
                    Add photo proof (required, up to {MAX_RETURN_IMAGES})
                  </label>
                  <div className="od-review-images-row">
                    {returnImagePreviews.map((src, idx) => (
                      <div key={idx} className="od-review-image-thumb">
                        <img src={src} alt={`Return upload ${idx + 1}`} />
                        <button
                          type="button"
                          className="od-review-image-remove"
                          onClick={() => removeReturnImage(idx)}
                          aria-label="Remove image"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    {returnImages.length < MAX_RETURN_IMAGES && (
                      <label
                        htmlFor="return-image-input"
                        className="od-review-image-add-btn"
                      >
                        <BsCamera />
                        <span>Add photo</span>
                      </label>
                    )}
                  </div>
                  <input
                    type="file"
                    id="return-image-input"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleReturnImageSelect}
                  />
                </div>
              </div>
              <div className="od-modal-actions">
                <button
                  type="button"
                  className="od-btn-cancel"
                  onClick={closeReturnModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="od-btn-submit"
                  onClick={handleReturnSubmit}
                  disabled={returnSubmitting}
                >
                  {returnSubmitting ? "Sending..." : "Submit request"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Review Image Lightbox ── */}
      {previewImage && (
        <div
          className="od-image-lightbox-overlay"
          onClick={() => setPreviewImage(null)}
        >
          <button
            className="od-image-lightbox-close"
            onClick={() => setPreviewImage(null)}
            aria-label="Close"
          >
            ✕
          </button>
          <img
            src={previewImage}
            alt="Review full size"
            className="od-image-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {lightboxImage && (
        <div
          className="od-image-lightbox-overlay"
          onClick={() => setLightboxImage(null)}
        >
          <button
            type="button"
            className="od-image-lightbox-close"
            onClick={() => setLightboxImage(null)}
            aria-label="Close image preview"
          >
            <BsX />
          </button>
          <img
            src={lightboxImage}
            alt="Preview"
            className="od-image-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default OrderDetails;
